/**
 * Datumrekenwerk op kalenderdagen (SPEC §5.1).
 *
 * Alles hier werkt op tekst in de vorm `YYYY-MM-DD` en rekent in UTC, zodat
 * zomertijd en tijdzones er niet tussen kunnen komen.
 */

export type Datum = string;

function alsUtc(datum: Datum): Date {
  const [j, m, d] = datum.split('-').map(Number);
  if (!j || !m || !d) throw new Error(`Geen datum: ${datum}`);
  return new Date(Date.UTC(j, m - 1, d));
}

function alsTekst(moment: Date): Datum {
  return moment.toISOString().slice(0, 10);
}

export function plusDagen(datum: Datum, dagen: number): Datum {
  const d = alsUtc(datum);
  d.setUTCDate(d.getUTCDate() + dagen);
  return alsTekst(d);
}

/** Dezelfde dag, `jaren` later; 29 februari wordt 28 februari. */
export function plusJaren(datum: Datum, jaren: number): Datum {
  const d = alsUtc(datum);
  const dag = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCFullYear(d.getUTCFullYear() + jaren);
  const laatste = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(dag, laatste));
  return alsTekst(d);
}

/** `tot` min `van`, in hele dagen. Negatief als `tot` eerder ligt. */
export function dagenTussen(van: Datum, tot: Datum): number {
  return Math.round((alsUtc(tot).getTime() - alsUtc(van).getTime()) / 86_400_000);
}

export function jaarVan(datum: Datum): number {
  return Number(datum.slice(0, 4));
}

export function maandVan(datum: Datum): number {
  return Number(datum.slice(5, 7));
}

/** De periode van een heldenjaar: het kalenderjaar, halfopen `[1 jan, 1 jan)`. */
export function kalenderjaarPeriode(jaar: number): { van: Datum; tot: Datum } {
  return { van: `${jaar}-01-01`, tot: `${jaar + 1}-01-01` };
}

/**
 * Voor welk kalenderjaar telt een aanmelding op `datum`? Het lopende jaar,
 * tenzij de maand op of na `vanafMaand` ligt: dan het volgende (SPEC §5.1).
 * `vanafMaand` 13 betekent: nooit.
 */
export function eersteKalenderjaar(datum: Datum, vanafMaand: number): number {
  return jaarVan(datum) + (maandVan(datum) >= vanafMaand ? 1 : 0);
}

const MAANDEN = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

/** `2026-03-14` → `14 maart 2026`. */
export function alsNederlandseDatum(datum: Datum): string {
  const [j, m, d] = datum.split('-').map(Number);
  if (!j || !m || !d) return datum;
  return `${d} ${MAANDEN[m - 1]} ${j}`;
}
